import {
  type CellData,
  type ColumnDef,
  type ColumnVisibilityState,
  flexRender,
  functionalUpdate,
  type RowData,
  type SortingState,
  type TableFeatures,
  useTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import { cn } from "~/lib/utils";
import { type DataTableFeatures, dtFeatures } from "./data-table";

declare module "@tanstack/react-table" {
  // Per-column styling hooks. `cellClassName` lets a column opt into a fixed
  // width + ellipsis (e.g. `truncate`) instead of growing with its content.
  interface ColumnMeta<TFeatures extends TableFeatures, TData extends RowData, TValue extends CellData = CellData> {
    /**
     * Column width, honoured when the table uses `layout="fixed"`. A number is
     * a fixed px width; a string is a plain CSS width such as `"16%"`. Note that
     * Chrome's fixed table layout ignores math functions mixing % and px
     * (`min()`/`clamp()`) — for a fluid width with a px cap use `colClassName`
     * with responsive utilities instead, e.g. `"w-[16%] xl:w-[200px]"`.
     */
    width?: number | string;
    /**
     * Classes for the column's `<col>` element (fixed layout only), e.g.
     * breakpoint-dependent width utilities.
     */
    colClassName?: string;
    /** Hide the column (col/th/td) below this Tailwind breakpoint. */
    hiddenBelow?: "sm" | "md" | "lg";
    cellClassName?: string;
    headerClassName?: string;
  }
}

// Static map so Tailwind's scanner sees the literal class names.
const HIDDEN_BELOW_CLASS = {
  sm: "max-sm:hidden",
  md: "max-md:hidden",
  lg: "max-lg:hidden",
} as const;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface RemoteDataTableProps<TData extends RowData> {
  columns: ColumnDef<DataTableFeatures, TData, any>[];
  /** The current page of rows, already fetched/sorted/filtered server-side. */
  data: TData[];
  /** Total row count across all pages (from the server aggregate). */
  rowCount: number;
  /** Use when the server can answer only whether a next page exists, not the exact total. */
  hasNextPage?: boolean;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange?: (next: number) => void;
  /** Controlled sorting state. Manual — the parent turns it into query variables. */
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  columnVisibility?: ColumnVisibilityState;
  onColumnVisibilityChange?: (next: ColumnVisibilityState) => void;
  emptyMessage?: ReactNode;
  className?: string;
  /** Dim the body while a page/sort change is in flight (e.g. inside a transition). */
  isPending?: boolean;
  /** When provided, each row becomes clickable and invokes this with the row's data. */
  onRowClick?: (row: TData) => void;
  /**
   * Column-width strategy. `"auto"` (default) sizes each column to its content.
   * `"fixed"` honours each column's `meta.width` (px) via a `<colgroup>` so widths
   * stay stable; columns whose `meta.cellClassName` includes `truncate` then
   * ellipsis their overflow.
   */
  layout?: "auto" | "fixed";
}

/**
 * Server-paginated sibling of {@link DataTable}. Pagination, sorting, and
 * filtering are all `manual` — this wrapper renders only the current page the
 * server returned and drives query variables through the controlled callbacks.
 * Base markup and tokens mirror `data-table.tsx`; the server table additionally
 * carries opt-in extras (`layout="fixed"`, `onRowClick`, per-column `meta`).
 */
export function RemoteDataTable<TData extends RowData>({
  columns,
  data,
  rowCount,
  hasNextPage,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  emptyMessage,
  className,
  isPending,
  onRowClick,
  layout = "auto",
}: RemoteDataTableProps<TData>) {
  const { t } = useTranslation();

  const state = useMemo(
    () => ({
      sorting,
      pagination: { pageIndex, pageSize },
      ...(columnVisibility !== undefined ? { columnVisibility } : {}),
    }),
    [sorting, pageIndex, pageSize, columnVisibility],
  );

  const onSortingChangeAdapter = useCallback(
    (updater: unknown) => {
      onSortingChange(functionalUpdate(updater as never, sorting) as SortingState);
    },
    [onSortingChange, sorting],
  );

  const onColumnVisibilityChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onColumnVisibilityChange) return;
      onColumnVisibilityChange(functionalUpdate(updater as never, columnVisibility ?? {}) as ColumnVisibilityState);
    },
    [onColumnVisibilityChange, columnVisibility],
  );

  const table = useTable<DataTableFeatures, TData>({
    features: dtFeatures,
    state,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount,
    onSortingChange: onSortingChangeAdapter,
    onColumnVisibilityChange: onColumnVisibilityChange ? onColumnVisibilityChangeAdapter : undefined,
    data,
    columns,
  });

  const rows = table.getRowModel().rows;
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const isFixed = layout === "fixed";
  const leafColumns = table.getVisibleLeafColumns();
  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
  const canPrevious = pageIndex > 0;
  const canNext = hasNextPage ?? pageIndex + 1 < pageCount;
  const rangeFrom = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeTo = Math.min(rowCount, (pageIndex + 1) * pageSize);
  // Show "and more" only when the count lags behind the server's next-page signal.
  const rangeLabel =
    !hasNextPage || pageIndex + 1 < pageCount
      ? t("ui.pagination.range", {
          from: rangeFrom,
          to: rangeTo,
          total: rowCount,
        })
      : t("ui.pagination.rangeWithMore", {
          from: rangeFrom,
          to: rangeTo,
        });

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
        <table className={cn("w-full border-collapse", isFixed ? "table-fixed" : "min-w-max")}>
          {isFixed && (
            <colgroup>
              {leafColumns.map((column) => {
                const { width, hiddenBelow, colClassName } = column.columnDef.meta ?? {};
                return (
                  <col
                    key={column.id}
                    className={cn(hiddenBelow && HIDDEN_BELOW_CLASS[hiddenBelow], colClassName)}
                    style={width != null ? { width } : undefined}
                  />
                );
              })}
            </colgroup>
          )}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-bg-subtle">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2",
                        header.column.columnDef.meta?.hiddenBelow &&
                          HIDDEN_BELOW_CLASS[header.column.columnDef.meta.hiddenBelow],
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1.5 text-left uppercase tracking-[0.06em] text-fg-2 hover:text-fg-1 focus:outline-none focus-visible:text-primary-700"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : sorted === "desc" ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-60" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={cn(isPending && "opacity-60 transition-opacity")}>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnCount} className="px-3.5 py-10 text-center text-[13px] text-fg-3">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    "border-b border-border-default last:border-b-0 hover:bg-primary-50",
                    onRowClick && "cursor-pointer",
                  )}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-3.5 py-3 text-[13px] align-middle",
                        cell.column.columnDef.meta?.hiddenBelow &&
                          HIDDEN_BELOW_CLASS[cell.column.columnDef.meta.hiddenBelow],
                        cell.column.columnDef.meta?.cellClassName,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-[12px] text-fg-3">
        <div className="flex items-center gap-3">
          <div className="tabular-nums">{rangeLabel}</div>
          {onPageSizeChange && (
            <div className="flex items-center gap-1.5">
              <span>{t("ui.pagination.rowsPerPage")}</span>
              <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                <SelectTrigger className="h-7 w-[72px]" aria-label={t("ui.pagination.rowsPerPage")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={String(opt)}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageIndexChange(pageIndex - 1)}
            disabled={!canPrevious}
          >
            <ChevronLeft />
            <span>{t("ui.pagination.previous")}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageIndexChange(pageIndex + 1)}
            disabled={!canNext}
          >
            <span>{t("ui.pagination.next")}</span>
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
